import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Shield, 
  ArrowRight, 
  Calculator, 
  FileText, 
  CheckCircle2, 
  Info, 
  ChevronDown,
  DollarSign,
  Clock,
  Percent,
  AlertCircle
} from 'lucide-react';
import { bankData, type BankId } from '../../data/bankData';
import LoanOnboarding from '../../components/LoanOnboarding';

const BankCredits = () => {
  const { bankId } = useParams<{ bankId: string }>();
  const bank = bankData[bankId as BankId];
  const [selectedCredit, setSelectedCredit] = useState<string | null>(null);
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const [expandedDetails, setExpandedDetails] = useState<string[]>([]);

  if (!bank) {
    return <div>Banka bulunamadı</div>;
  }

  const toggleDetails = (creditTitle: string) => {
    setExpandedDetails(prev => 
      prev.includes(creditTitle)
        ? prev.filter(title => title !== creditTitle)
        : [...prev, creditTitle]
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#F7BA45]/20 to-slate-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-6 mb-8">
              <img 
                src={bank.logo} 
                alt={bank.name}
                className="h-16 object-contain"
              />
              <div>
                <h1 className="text-3xl font-bold text-slate-800 mb-2">
                  {bank.name} Kredi Ürünleri
                </h1>
                <p className="text-slate-600">
                  Size en uygun {bank.name} kredisini seçin, hemen başvurun
                </p>
              </div>
            </div>

            {/* Bank Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="text-sm text-slate-500 mb-1">Müşteri Sayısı</div>
                <div className="text-xl font-bold text-slate-800">{bank.stats.customers}</div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="text-sm text-slate-500 mb-1">Şube Sayısı</div>
                <div className="text-xl font-bold text-slate-800">{bank.stats.branches}</div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="text-sm text-slate-500 mb-1">ATM Sayısı</div>
                <div className="text-xl font-bold text-slate-800">{bank.stats.atmCount}</div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="text-sm text-slate-500 mb-1">Kuruluş Yılı</div>
                <div className="text-xl font-bold text-slate-800">{bank.stats.founded}</div>
              </div>
            </div>

            {/* Credit Products */}
            <div className="space-y-6">
              {bank.products.credits.map((credit, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-[#F7BA45]/50 transition-all duration-300 hover:shadow-lg"
                >
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-slate-800 mb-6">{credit.title}</h3>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                          <Percent size={16} className="text-[#F7BA45]" />
                          Faiz Oranı
                        </div>
                        <div className="font-semibold text-slate-800">%{credit.rate}</div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                          <Clock size={16} className="text-[#F7BA45]" />
                          Vade
                        </div>
                        <div className="font-semibold text-slate-800">{credit.term} Ay</div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                          <DollarSign size={16} className="text-[#F7BA45]" />
                          Tutar
                        </div>
                        <div className="font-semibold text-slate-800">{credit.amount} TL</div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                          <Calculator size={16} className="text-[#F7BA45]" />
                          Aylık Taksit
                        </div>
                        <div className="font-semibold text-slate-800">4.771 TL</div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div className="space-y-3">
                        <h4 className="font-medium text-slate-800 flex items-center gap-2">
                          <Shield size={16} className="text-[#F7BA45]" />
                          Avantajlar
                        </h4>
                        {credit.benefits.map((benefit, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                            <CheckCircle2 size={16} className="text-[#F7BA45]" />
                            <span>{benefit}</span>
                          </div>
                        ))}
                      </div>
                      <div className="space-y-3">
                        <h4 className="font-medium text-slate-800 flex items-center gap-2">
                          <FileText size={16} className="text-[#F7BA45]" />
                          Gerekli Belgeler
                        </h4>
                        {credit.documents.map((doc, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                            <CheckCircle2 size={16} className="text-[#F7BA45]" />
                            <span>{doc}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Info size={16} className="text-[#F7BA45]" />
                        <span>Başvuru için {bank.name}'a yönlendirilirsiniz</span>
                      </div>
                      <div className="flex items-center gap-3 w-full sm:w-auto">
                        <button
                          onClick={() => {
                            setSelectedCredit(credit.title);
                            setIsCalculatorOpen(true);
                          }}
                          className="flex-1 sm:flex-none px-6 py-2.5 border-2 border-[#F7BA45] text-[#F7BA45] hover:bg-[#F7BA45]/5 rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                          <Calculator size={18} />
                          Hesapla
                        </button>
                        <button className="flex-1 sm:flex-none bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 px-6 py-2.5 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-md hover:shadow-lg">
                          HEMEN BAŞVUR
                          <ArrowRight size={18} />
                        </button>
                      </div>
                    </div>

                    {/* Expandable Requirements Section */}
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <button
                        onClick={() => toggleDetails(credit.title)}
                        className="w-full flex items-center justify-between text-sm font-medium text-slate-600 hover:text-slate-900"
                      >
                        <div className="flex items-center gap-2">
                          <AlertCircle size={16} className="text-[#F7BA45]" />
                          <span>Başvuru Koşulları</span>
                        </div>
                        <ChevronDown 
                          size={18} 
                          className={`transition-transform duration-300 ${
                            expandedDetails.includes(credit.title) ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      
                      <div className={`overflow-hidden transition-all duration-300 ${
                        expandedDetails.includes(credit.title) ? 'max-h-96 mt-4' : 'max-h-0'
                      }`}>
                        <div className="space-y-2">
                          {credit.requirements.map((req, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                              <CheckCircle2 size={16} className="text-[#F7BA45]" />
                              <span>{req}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Loan Calculator Modal */}
      {selectedCredit && (
        <LoanOnboarding
          isOpen={isCalculatorOpen}
          onClose={() => {
            setIsCalculatorOpen(false);
            setSelectedCredit(null);
          }}
          loanAmount={50000}
          loanTerm={12}
        />
      )}
    </div>
  );
};

export default BankCredits;